import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { findExistingCustomer, updateCustomerFromBooking } from '../lib/customers';
import { supabase } from '../lib/supabase';
import { renderEmailTemplateToHtml } from '../lib/emailTemplateRenderer';
import { triggerFormSubmissionAutomations } from '../lib/automationsV2';

interface BookingPagePopupProps {
  isEnabled: boolean;
  delaySeconds: number;
  durationSeconds: number;
  message: string;
  buttonText: string;
  buttonAction: 'url' | 'lead_form';
  buttonUrl: string;
  sendEmail: boolean;
  emailTemplateId: string;
  businessId: string;
}

export default function BookingPagePopup({ 
  isEnabled, 
  delaySeconds, 
  durationSeconds, 
  message,
  buttonText,
  buttonAction,
  buttonUrl,
  sendEmail,
  emailTemplateId,
  businessId 
}: BookingPagePopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormData, setLeadFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [businessData, setBusinessData] = useState<any>(null);

  // Fetch business data when component mounts
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!businessId) return;
      
      try {
        const { data: business, error } = await supabase
          .from('businesses')
          .select('id, name, booking_settings')
          .eq('id', businessId)
          .single();
          
        if (error) {
          console.error('Error fetching business data:', error);
          return;
        }
        
        console.log('📊 POPUP: Fetched business data:', {
          id: business.id,
          name: business.name,
          hasLogo: !!business.booking_settings?.logo_url
        });
        
        setBusinessData(business);
      } catch (error) {
        console.error('Error fetching business data:', error);
      }
    };
    
    fetchBusinessData();
  }, [businessId]);

  useEffect(() => {
    if (!isEnabled || hasShown) return;

    // Set up the delay timer
    const delayTimer = setTimeout(() => {
      setIsVisible(true);
      setHasShown(true);
    }, delaySeconds * 1000);

    return () => clearTimeout(delayTimer);
  }, [isEnabled, delaySeconds, hasShown]);

  // Separate useEffect for auto-hide timer
  useEffect(() => {
    if (!isVisible || hasUserInteracted) return;

    // Set up the duration timer to auto-hide only if user hasn't interacted
    const durationTimer = setTimeout(() => {
      // Double-check user hasn't interacted before hiding
      if (!hasUserInteracted) {
        setIsVisible(false);
      }
    }, durationSeconds * 1000);

    return () => clearTimeout(durationTimer);
  }, [isVisible, durationSeconds, hasUserInteracted]);

  const handleClose = () => {
    setIsVisible(false);
    setShowLeadForm(false);
    setShowSuccessMessage(false);
    setLeadFormData({ first_name: '', last_name: '', email: '', phone: '' });
    setHasUserInteracted(false);
  };

  const handlePopupInteraction = () => {
    // Mark that user has interacted with the popup
    setHasUserInteracted(true);
  };

  const handleInputChange = (field: 'first_name' | 'last_name' | 'email' | 'phone', value: string) => {
    setLeadFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleButtonClick = async () => {
    handlePopupInteraction();
    
    if (buttonAction === 'url' && buttonUrl) {
      // Track URL click as banner submission
      try {
        console.log('📊 BANNER TRACKING: Recording URL click submission');
        await supabase
          .from('banner_submissions')
          .insert({
            business_id: businessId,
            submission_type: 'url_click',
            submitted_at: new Date().toISOString()
          });
        console.log('✅ BANNER TRACKING: URL click recorded successfully');
      } catch (error) {
        console.error('❌ BANNER TRACKING: Error recording URL click:', error);
      }
      
      // Open URL in new tab
      window.open(buttonUrl, '_blank');
      // Don't close the popup - let it stay open indefinitely until manually closed
    } else if (buttonAction === 'lead_form') {
      // Show lead form
      setShowLeadForm(true);
    }
  };

  const handleLeadFormSubmit = async (e: React.FormEvent) => {
    console.log('🚨 DEBUG: handleLeadFormSubmit called!');
    console.log('🚨 DEBUG: Form data:', leadFormData);
    console.log('🚨 DEBUG: Business ID:', businessId);
    console.log('🚨 DEBUG: Supabase available:', !!supabase);
    
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!supabase || !businessId) {
        console.error('🚨 DEBUG: Missing supabase or businessId!', { supabase: !!supabase, businessId });
        throw new Error('Database connection or business ID not available');
      }

      // Extract customer data from form
      const customerData = {
        first_name: leadFormData.first_name,
        last_name: leadFormData.last_name,
        email: leadFormData.email,
        phone: leadFormData.phone
      };

      console.log('🎪 POPUP LEAD FORM: Extracted customer data:', customerData);

      // Create or find customer using the same logic as lead forms
      let customerId: string | null = null;

      if (customerData.email || customerData.phone) {
        console.log('🔍 POPUP LEAD FORM: Looking for existing customer with:', {
          business_id: businessId,
          email: customerData.email,
          phone: customerData.phone
        });

        const existingCustomer = await findExistingCustomer(
          businessId,
          customerData.email,
          customerData.phone
        );

        if (existingCustomer) {
          console.log('👤 POPUP LEAD FORM: Found existing customer:', {
            id: existingCustomer.id,
            first_name: existingCustomer.first_name,
            last_name: existingCustomer.last_name,
            email: existingCustomer.email,
            phone: existingCustomer.phone
          });
          
          // Update the existing customer with any new information
          const updatedCustomer = await updateCustomerFromBooking(
            existingCustomer.id,
            {
              first_name: customerData.first_name,
              last_name: customerData.last_name,
              email: customerData.email,
              phone: customerData.phone
            }
          );
          
          customerId = existingCustomer.id;
          console.log('✅ POPUP LEAD FORM: Updated existing customer:', updatedCustomer);
        } else {
          // Create a new customer
          console.log('🆕 POPUP LEAD FORM: No matching customer found. Creating new customer');
          const { data: newCustomer, error: customerError } = await supabase
            .from('customers')
            .insert({
              business_id: businessId,
              first_name: customerData.first_name,
              last_name: customerData.last_name,
              email: customerData.email,
              phone: customerData.phone,
              source: 'Online Booking Popup',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select('id')
            .single();

          if (customerError) {
            console.error('❌ POPUP LEAD FORM: Error creating customer:', customerError);
            throw new Error(`Failed to create customer: ${customerError.message}`);
          }
          
          if (!newCustomer?.id) {
            throw new Error('Failed to create customer record');
          }
          
          customerId = newCustomer.id;
          console.log('✨ POPUP LEAD FORM: Created new customer:', customerId);
        }
      } else {
        console.log('⚠️ POPUP LEAD FORM: No email or phone provided, skipping customer creation');
      }

      // Create lead form submission record (same as regular lead forms)
      const submissionData = {
        business_id: businessId,
        lead_form_id: null, // Online booking banner doesn't have a specific form ID
        form_data: {
          ...leadFormData,
          form_type: 'online_booking_banner',
          source: 'Online Booking Popup'
        },
        submitted_at: new Date().toISOString(),
        customer_id: customerId // Link to the customer we created
      };

      console.log('📝 POPUP BANNER: Creating lead form submission:', submissionData);

      const { data: createdSubmission, error: submissionError } = await supabase
        .from('lead_form_submissions')
        .insert(submissionData)
        .select('id')
        .single();

      if (submissionError) {
        console.error('❌ POPUP BANNER: Error creating lead form submission:', submissionError);
        throw submissionError;
      }

      console.log('✅ POPUP BANNER: Created lead form submission successfully:', createdSubmission?.id);

      // Track form submission as banner submission
      try {
        console.log('📊 BANNER TRACKING: Recording form submission');
        await supabase
          .from('banner_submissions')
          .insert({
            business_id: businessId,
            submission_type: 'form_submit',
            customer_id: customerId,
            submitted_at: new Date().toISOString()
          });
        console.log('✅ BANNER TRACKING: Form submission recorded successfully');
      } catch (trackingError) {
        console.error('❌ BANNER TRACKING: Error recording form submission:', trackingError);
        // Don't throw here - continue with email and automation even if tracking fails
      }

      // Send email if enabled
      if (sendEmail && emailTemplateId) {
        console.log('📧 POPUP BANNER: Sending email using template:', emailTemplateId);
        try {
          // Fetch the email template
          const { data: template, error: templateError } = await supabase
            .from('email_templates')
            .select('*')
            .eq('id', emailTemplateId)
            .eq('business_id', businessId)
            .single();

          if (templateError || !template) {
            console.error('❌ POPUP BANNER: Template not found:', templateError);
            throw new Error('Email template not found');
          }

          console.log('📧 POPUP BANNER: Template fetched:', template.name);

          // Process email template with customer data
          let emailSubject = template.subject || 'Thank you for your interest!';
          let emailBody = '';

          // Replace variables in subject
          emailSubject = emailSubject
            .replace(/{customer_name}/g, `${leadFormData.first_name} ${leadFormData.last_name}`.trim())
            .replace(/{first_name}/g, leadFormData.first_name || '')
            .replace(/{last_name}/g, leadFormData.last_name || '');

          // Process template content
          if (template.content && template.content.sections) {
            const customerName = `${leadFormData.first_name} ${leadFormData.last_name}`.trim();
            emailBody = renderEmailTemplateToHtml({
              sections: template.content.sections,
              businessName: businessData?.name || 'Your Business',
              logoUrl: businessData?.booking_settings?.logo_url,
              variables: {
                '{customer_name}': customerName,
                '{first_name}': leadFormData.first_name || '',
                '{last_name}': leadFormData.last_name || '',
              },
            });
          } else {
            // Fallback simple email with proper business branding
            let headerContent = '';
            if (businessData?.booking_settings?.logo_url) {
              headerContent = `<div style="text-align: center; margin-bottom: 20px;"><img src="${businessData.booking_settings.logo_url}" alt="${businessData.name} logo" style="max-height: 60px; max-width: 200px; object-fit: contain;" /></div>`;
            } else if (businessData?.name) {
              headerContent = `<div style="text-align: center; margin-bottom: 20px;"><h1 style="color: #6366f1; margin: 0;">${businessData.name}</h1></div>`;
            }
            
            emailBody = `
              <html>
              <body style="font-family: Arial, sans-serif; padding: 20px;">
                ${headerContent}
                <h2>Thank you for your interest, ${leadFormData.first_name}!</h2>
                <p>We've received your information and will be in touch soon with your discount code.</p>
                <p>Best regards,<br>${businessData?.name ? `The ${businessData.name} Team` : 'The Team'}</p>
              </body>
              </html>
            `;
          }

          console.log('📧 POPUP BANNER: Processed email content', {
            subject: emailSubject,
            bodyLength: emailBody.length
          });

          // Use the Gmail edge function with processed template
          const { data: emailResult, error: emailError } = await supabase.functions.invoke('gmail-send-email', {
            body: {
              to: leadFormData.email,
              subject: emailSubject,
              body: emailBody,
              businessId: businessId
            }
          });

          if (emailError) {
            console.error('❌ POPUP BANNER: Gmail send error:', emailError);
          } else {
            console.log('✅ POPUP BANNER: Email sent successfully using template:', emailResult);
          }
        } catch (emailError) {
          console.error('❌ POPUP BANNER: Error sending email:', emailError);
          // Don't throw here - continue with automation even if email fails
        }
      }

      // Trigger online booking banner automations (non-blocking)
      if (businessId) {
        try {
          console.log('🎯 POPUP BANNER: Triggering online booking banner automations');
          console.log('🎯 POPUP BANNER: Business ID:', businessId);
          console.log('🎯 POPUP BANNER: Form data:', leadFormData);
          console.log('🎯 POPUP BANNER: Submission ID:', createdSubmission?.id);
          
          // Prepare enhanced form data with additional context
          const enhancedFormData = {
            ...leadFormData,
            customer_id: customerId, // Pass the customer ID to automation
            submission_id: createdSubmission?.id, // Pass the submission ID
            form_type: 'online_booking_banner',
            submitted_at: new Date().toISOString(),
            source: 'Online Booking Popup'
          };
          
          triggerFormSubmissionAutomations(businessId, 'online_booking_banner', enhancedFormData)
            .then(() => console.log('🤖 POPUP BANNER: Automations completed successfully'))
            .catch(automationError => console.error('❌ POPUP BANNER: Error triggering automations:', automationError));
          
          console.log('🤖 POPUP BANNER: Automation trigger initiated');
        } catch (automationError) {
          console.error('❌ POPUP BANNER: Failed to trigger automations:', automationError);
          // We don't throw here to avoid blocking the form submission flow
        }
      }
      
      // Show success message
      console.log('🎉 POPUP LEAD FORM: Lead submitted successfully:', {
        leadData: leadFormData,
        customerId: customerId
      });
      
      // Show success message instead of closing
      setShowSuccessMessage(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error('❌ POPUP LEAD FORM: Error submitting lead:', error);
      // Still close the popup even if there's an error
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 left-4 right-4 z-50 animate-slide-down">
      <div className="max-w-4xl mx-auto">
        <div 
          className="bg-white text-gray-900 shadow-2xl rounded-2xl border border-gray-200 backdrop-blur-sm"
          onClick={handlePopupInteraction}
        >
          <div className="relative overflow-hidden rounded-2xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gray-50 bg-opacity-50">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}></div>
            </div>
            
            <div className="relative px-4 sm:px-6 py-4 sm:py-5">
            {showSuccessMessage ? (
              /* Success Message */
              <div className="text-center py-6">
                <div className="mb-4">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Thank you!</h3>
                <p className="text-gray-600 mb-6">Check your email for the discount code.</p>
                <button
                  onClick={handleClose}
                  className="inline-flex items-center px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded-lg hover:bg-green-600 transition-colors duration-200 shadow-lg"
                >
                  Close
                </button>
              </div>
            ) : !showLeadForm ? (
              <>
            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center justify-between">
              {/* Left side - Icon and Message */}
              <div className="flex items-center space-x-4 flex-1">

                
                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      Special Offer
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 mt-1 leading-tight">
                    {message}
                  </p>
                </div>
              </div>

              {/* Right side - CTA and Close */}
              <div className="flex items-center space-x-3 ml-4">
                                 {/* Call to Action Button */}
                 <button 
                   onClick={handleButtonClick}
                   className="inline-flex items-center px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded-lg hover:bg-green-600 transition-colors duration-200 shadow-lg"
                 >
                   <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                   </svg>
                   {buttonText}
                 </button>
                
                {/* Close Button */}
                <button
                  onClick={handleClose}
                  className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  aria-label="Close banner"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="sm:hidden">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                    Special Offer
                  </span>
                </div>
                <button
                  onClick={handleClose}
                  className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 focus:outline-none"
                  aria-label="Close banner"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              <p className="text-base font-semibold text-gray-900 mb-3 leading-tight">
                {message}
              </p>
              
              <button 
                onClick={handleButtonClick}
                className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-green-500 text-white text-sm font-semibold rounded-lg hover:bg-green-600 transition-colors duration-200 shadow-lg"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                {buttonText}
              </button>
            </div>
            </>
            ) : (
              /* Lead Capture Form */
              <form onSubmit={handleLeadFormSubmit} className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600">
                    We'll email you your discount code
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="text"
                        placeholder="First Name"
                        value={leadFormData.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        onFocus={handlePopupInteraction}
                        required
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={leadFormData.last_name}
                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                        onFocus={handlePopupInteraction}
                        required
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={leadFormData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onFocus={handlePopupInteraction}
                      required
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      placeholder="Your Phone Number"
                      value={leadFormData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      onFocus={handlePopupInteraction}
                      required
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      handlePopupInteraction();
                      setShowLeadForm(false);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors duration-200 border border-gray-300"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded-lg hover:bg-green-600 transition-colors duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
            
            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 via-green-500 to-green-600 rounded-b-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


